import {get, post} from "./index.js";
import moment from "moment";

async function getReservations(page, filters) {
    return await get(`/reservation/getLandlordReservations`, {page, filters})
}

async function getReservationById(reservation_id) {
    return await get(`/reservation/getLandlordReservation/${reservation_id}`)
}

async function acceptReservation(reservation_id) {
    return await post("/reservation/acceptReservation/", {reservation_id})
}

const rejectReservation = async (fields) => {
    return await post ("/reservation/rejectReservation", fields)
}

const suggestNewDates = async (fields) => {
    return await post ("/reservation/suggestNewDates", fields)
}

const suggestNewProperty = async (fields) => {
    return await post ("/reservation/suggestNewProperty", fields)
}

const cancelReservation = async (fields) => {
    return await post ("/reservation/cancellationRequestLandlord", fields)
}

/**
 * The extra tenants value is a monthly amount, just like the rent itself: it is charged per extra
 * tenant, per month. It must always be folded into the monthly rent *before* that rent is prorated
 * (daily / biweekly), never added on top of an already prorated value.
 */
const getMonthlyExtraTenantsValue = (extraTenantsPrice, numTenants) => {
    if (!extraTenantsPrice || !numTenants || numTenants <= 1) return 0;
    return extraTenantsPrice * (numTenants - 1);
}

const getMonthlyRent = (reservation) => {
    let {fixedRent, rentValue, rentMonths, discount, extraTenantsPrice} = (reservation.payment || {});
    //payment.extraTenantsValue is the prorated share charged with the first rent, so it cannot be
    //used here: this is a *monthly* rent, and needs the monthly extra tenants value. Reservations
    //booked before extraTenantsPrice started being snapshotted did store the monthly value there.
    let extraTenantsValue = extraTenantsPrice ?
        getMonthlyExtraTenantsValue(extraTenantsPrice, reservation.numTenants)
        : ((reservation.payment || {}).extraTenantsValue || 0);
    if(fixedRent && !rentValue || (!fixedRent && (!rentMonths || Object.keys(rentMonths).length !== 12))) return "N/A";
    if (fixedRent) {
        let rent = (discount ? (rentValue - (rentValue * discount)) : rentValue) + extraTenantsValue;
        if(rent % 1 !== 0) rent = rent.toFixed(2);
        return rent + "€";
    }
    const _toMonth = (m) => moment(m).locale('en').format("MMMM").toLowerCase();
    let startMoveIn = moment(reservation.moveIn);
    let moveOutMoment = moment(reservation.moveOut);
    let min, max;
    while (startMoveIn.isBefore(moveOutMoment)) {
        let curMonthValue = parseInt(rentMonths[_toMonth(startMoveIn)]);
        if (!min || curMonthValue < min) min = curMonthValue;
        if (!max || curMonthValue > max) max = curMonthValue;
        startMoveIn.add(1, "month");
    }
    //only the rent itself is discounted, never the extra tenants value, matching the fixedRent
    //branch above and the amounts actually billed
    if(discount) {
        min -= min * discount;
        max -= max * discount;
    }
    min += extraTenantsValue;
    max += extraTenantsValue;
    if (min === max) return "€" + min;
    return (min + " - " + max + "€");
}
const getCancellationFee = (reservation) => {
    let preWarningDays = moment(reservation.moveIn).startOf("day").diff(moment(reservation.cancellation.requestDate || new Date()).startOf("day"), "days");
    let fee = 0;
    if(preWarningDays <= 30) fee = 100 * 1.23;
    if(preWarningDays === 0) fee = 250 * 1.23; //no dia do check-in
    return fee.toFixed(2);
}

/** Daily contracts are billed per night: the move out day is not billed. */
export const getStayNights = (moveIn, moveOut) => {
    return Math.max(moment(moveOut).startOf("day").diff(moment(moveIn).startOf("day"), "days"), 0);
}

/** How many of the stay's nights fall inside the calendar month of monthMoment. */
export const getNightsInMonth = (moveIn, moveOut, monthMoment) => {
    let monthStart = monthMoment.clone().startOf("month");
    let nextMonthStart = monthStart.clone().add(1, "month");
    let stayStart = moment(moveIn).startOf("day");
    let stayEnd = moment(moveOut).startOf("day"); //exclusive: the move out day is not billed
    let segmentStart = stayStart.isAfter(monthStart) ? stayStart : monthStart;
    let segmentEnd = stayEnd.isBefore(nextMonthStart) ? stayEnd : nextMonthStart;
    return Math.max(segmentEnd.diff(segmentStart, "days"), 0);
}

/** A stay from the 1st to the last day of a single month is billed as a whole month. */
export const isWholeCalendarMonthStay = (moveIn, moveOut) => {
    let moveInMoment = moment(moveIn);
    let moveOutMoment = moment(moveOut);
    return moveInMoment.isSame(moveOutMoment, "month")
        && moveInMoment.date() === 1
        && moveOutMoment.date() === moveOutMoment.daysInMonth();
}

const generateFirstRentalPayment = (property, moveIn, moveOut, numTenants) => {
    if (!property) return {};
    let {incompleteRentType: rentType} = property;
    let monthlyExtraTenantsValue = getMonthlyExtraTenantsValue(property.room.extraTenantsPrice, numTenants);

    let moveInDate = new Date(moveIn);
    let moveOutDate = new Date(moveOut);
    let moveInMoment = moment(moveIn);
    let moveOutMoment = moment(moveOut);

    let mo = moveInMoment.clone().startOf('month');
    let currentMonthExt = mo.locale('en').format("MMMM").toLowerCase();
    let monthRent = parseInt(property.room.fixedRent ? property.room.rent : property.room.rentMonths[currentMonthExt]);

    let nextMo = mo.clone().add(1, 'M');

    let nextMonthRent = parseInt(property.room.fixedRent ? property.room.rent : property.room.rentMonths[nextMo.locale('en').format("MMMM").toLowerCase()]);

    //the monthly rent is the only input: whatever is part of it (rent, extra tenants) gets prorated
    //together by the exact same rule. Mirrors service/property_manager.js#generateFirstRentalPayment
    const computeRents = (monthRent, nextMonthRent) => {
        let calculatedRent;
        let secondMonthPayment = 0;

        if (rentType === 'daily') {
            let nights = getStayNights(moveIn, moveOut);
            if (moveOutMoment.isSame(moveInMoment, 'month')) {
                if(isWholeCalendarMonthStay(moveIn, moveOut)) {
                    calculatedRent = monthRent;
                } else {
                    calculatedRent = Math.ceil(monthRent / moveInMoment.daysInMonth() * nights)
                }
            } else {
                let diffStartOfMonth = moveInMoment.diff(moment(moveInMoment).startOf('month'), 'days');
                if(nights + 1 > moveInMoment.daysInMonth()) {
                    calculatedRent = monthRent;
                    let subNextMonth = monthRent / moveInMoment.daysInMonth() * diffStartOfMonth;
                    let adjustedNextMonthRent = nextMonthRent;
                    if(moveOutMoment.isSame(moveInMoment.clone().add(1, "month"), "month")) { //move out is next month
                        adjustedNextMonthRent = (nextMonthRent / moveOutMoment.daysInMonth()) * getNightsInMonth(moveIn, moveOut, moveOutMoment);
                    }
                    secondMonthPayment = Math.max(Math.round((adjustedNextMonthRent - subNextMonth)*100)/100, 0);
                } else {
                    let curMonthDailyRent = monthRent / moveInMoment.daysInMonth();
                    let nextMonthDailyRent = nextMonthRent / moveOutMoment.daysInMonth();
                    calculatedRent = Math.ceil(((curMonthDailyRent * getNightsInMonth(moveIn, moveOut, moveInMoment))
                        + (nextMonthDailyRent * getNightsInMonth(moveIn, moveOut, moveOutMoment)))*100)/100;
                }
            }
        } else if(rentType === 'biweekly') {
            let moveInHalf = moveInDate.getDate() >= 15;
            let moveOutHalf = moveOutDate.getDate() <= 15;
            if(moveInDate.getMonth() === moveOutDate.getMonth() && moveInDate.getFullYear() === moveOutDate.getFullYear()) {
                if (moveInHalf || moveOutHalf) {
                    calculatedRent = Math.ceil(monthRent / 2);
                } else {
                    calculatedRent = monthRent;
                }
            } else {
                if (moveInHalf) {
                    calculatedRent = Math.ceil(monthRent / 2 + nextMonthRent / 2);
                    if (!(nextMo.month() === moveOutDate.getMonth() && moveOutHalf)) {
                        secondMonthPayment = Math.ceil(nextMonthRent / 2);
                    }
                } else {
                    calculatedRent = monthRent;
                    if (nextMo.month() === moveOutDate.getMonth() && moveOutHalf) {
                        secondMonthPayment = Math.ceil(nextMonthRent / 2);
                    } else {
                        secondMonthPayment = nextMonthRent;
                    }
                }
            }
        } else {
            calculatedRent = monthRent;
            secondMonthPayment = nextMonthRent;
        }

        if(calculatedRent > monthRent) {
            secondMonthPayment = Math.round(secondMonthPayment + (calculatedRent - monthRent));
            calculatedRent = monthRent;
        }
        return {calculatedRent, secondMonthPayment};
    };

    let rentsWithoutExtraTenants = computeRents(monthRent, nextMonthRent);
    let rentsWithExtraTenants = monthlyExtraTenantsValue ?
        computeRents(monthRent + monthlyExtraTenantsValue, nextMonthRent + monthlyExtraTenantsValue)
        : rentsWithoutExtraTenants;

    let calculatedRent = rentsWithoutExtraTenants.calculatedRent;
    //extraTenantsValue holds the *prorated* share of the extra tenants price that this payment
    //covers, so that firstRent + extraTenantsValue is what is actually charged
    let extraTenantsValue = Math.round((rentsWithExtraTenants.calculatedRent - calculatedRent) * 100) / 100;
    let secondMonthPayment = Math.round((rentsWithExtraTenants.secondMonthPayment - extraTenantsValue) * 100) / 100;

    let discount;
    if(property.room.discount > 0 && (moveOutMoment.diff(moveInMoment, "month") >= 5)){
        discount = (property.room.discount / 100);
        calculatedRent -= calculatedRent * discount;
        secondMonthPayment -= secondMonthPayment * discount;
    }
    return {
        firstRent: calculatedRent,
        secondMonthPayment,
        extraTenantsValue,
        monthlyExtraTenantsValue,
        discount
    };
}
const getPaidAtMoveIn = (reservation) => {
    let dateRents = reservation.dateRents;
    let hasLastRent = reservation.property.paidAtMovinDate?.includes("Last Rent") && dateRents.length > 1;
    let lastRent = reservation.lastRent || 0;
    let securityDepositAmount = parseInt(reservation.securityDeposit.slice(1)) || 0;
    let paidAtMoveIn = hasLastRent ? (parseInt(lastRent.slice(1)) + securityDepositAmount) : securityDepositAmount;
    if(reservation.payment.extraAdministrationFee > 0) paidAtMoveIn += reservation.payment.extraAdministrationFee;
    return "€" + paidAtMoveIn.toFixed(2);
}


export const reservationServices = {
    getReservations,
    getReservationById,
    acceptReservation,
    rejectReservation,
    cancelReservation,
    suggestNewDates,
    suggestNewProperty,
    generateFirstRentalPayment,
    getMonthlyExtraTenantsValue,
    getMonthlyRent,
    getPaidAtMoveIn,
    getCancellationFee
};
